import { debounce, filter, get, map, range, uniqBy } from 'lodash'
import AppButton from '@theme/components/atom/AppButton'
import DetectMobileMixin from '~/sites/shop/mixins/DetectMobileMixin'
import ProductConfigurationMixin from '~/mixins/ProductConfigurationMixin'
import ProductConfigurator from '~/themes/base/components/product/ProductConfigurator'
import ProductConfiguratorGroups from '@theme/components/product/ProductConfigurator/ProductConfiguratorGroups'
import QuestionMark from '@theme/components/atom/QuestionMark'

export default {
  extends: ProductConfigurator,
  components: { AppButton, ProductConfiguratorGroups, QuestionMark },
  mixins: [ProductConfigurationMixin, DetectMobileMixin],
  props: {
    weight: Number,
    quantity: { type: Number, default: 1 },
    productParent: { type: Object, default: null },
    isStickyActiveExternal: Boolean,
    targetId: String,
  },
  data() {
    return {
      airShaft: {
        ids: ['634dc4f03b6b1100179ff39b', '666a03e15a80fa0018759df9', '6669fd365a80fa0018759df1'],
        options: map(range(30, 71), value => ({ text: `${value} cm`, value })),
      },
      airShaftValues: {},
      excludedSizes: [],
      sizesOpen: true,
      sizeIcons: { width: 'icon/switch-horizontal', height: 'icon/switch-vertical' },
      sizeVariants: [],
      colorPreselectedFromUrl: false,
      isValidatingSize: false,
      sizeValidationError: null,
    }
  },
  computed: {
    isAvailable() {
      return this.product.availability.isAvailable && this.product.availability.stock > 0
    },
    titleText() {
      return this.isAvailable ? this.$t('ProductConfigurator.title') : this.$t('ProductConfigurator.titleCustom')
    },
    isOneSizeVariant() {
      return this.product?.attributes?.cms_ids?.length === 1
    },
    hasHiddenSizeConfigurator() {
      return this.isOneSizeVariant
    },
    addToCartCustomData() {
      const configuration = []
      this.configuration.forEach(c => {
        if (c.selected) {
          const item = { code: c.code }
          if (this.airShaftValues[c.product_configuration_id] != null) {
            item.airShaftValue = this.airShaftValues[c.product_configuration_id]
          }
          configuration.push(item)
        }
      })

      this.groupedConfiguration.forEach(({ required, configurations }) => {
        const isRequiredAndEmpty = required && configurations.every(c => !c.selected)
        if (isRequiredAndEmpty) {
          const def = configurations.find(c => c.default_price)
          if (def) {
            const item = { code: def.code }
            const v = this.airShaftValues[def.product_configuration_id]
            if (v != null) item.airShaftValue = v
            configuration.push(item)
          }
        }
      })

      return {
        width: this.productWidth,
        height: this.productHeight,
        weight: this.weight,
        configuration,
        externalId: this.product.externalId,
      }
    },
    productSizeImage() {
      return (
        get(this.product, 'content.product_size_image[0].fileKey') ||
        get(this.relatedCategories, '[0].content.product_size_image[0].fileKey') ||
        get(this.$store, 'state.globals.configuration_products.product_size_image[0].path', false)
      )
    },
    sizeSelectorStep() {
      return (
        this.product?.content?.configuration_dimension_generate ??
        this.productParent?.content?.configuration_dimension_generate ??
        1
      )
    },
    sizesSorted() {
      return this.isMobileDetected ? Object.keys(this.sizes).reverse() : Object.keys(this.sizes)
    },
    sizesShow(vm) {
      return (
        vm.$themeSettings.components.ProductConfigurator.sizes.enable &&
        vm.configuration &&
        vm.configuration.length > 0 &&
        !vm.hasHiddenSizeConfigurator
      )
    },
    sizeLabel() {
      return `${this.sizes.width}cm x ${this.sizes.height}cm`
    },
    isExcluded() {
      const sizes = this.sizes
      // Include both excluded sizes and validation errors
      return Boolean(
        this.excludedSizes.find(item => +item.width === +sizes.width && +item.height === +sizes.height) ||
          this.sizeValidationError,
      )
    },
    mainImage() {
      return (this.displayedVariant && this.displayedVariant.mainImage) || this.product.mainImage
    },
    selectedParameters() {
      return (this.configuration || []).filter(i => i.selected)
    },
  },
  watch: {
    configuration: {
      immediate: true,
      handler(config) {
        const barvaCode = this.$route?.query?.barva
        if (this.colorPreselectedFromUrl || !Array.isArray(config) || !barvaCode) return

        const groups = this.groupedConfiguration
        if (!Array.isArray(groups)) return

        for (const group of groups) {
          const cfg = group.configurations.find(c => c.code === barvaCode)
          if (cfg) {
            const real = this.configuration.find(c => c.product_configuration_id === cfg.product_configuration_id)
            if (real) {
              real.selected = true
              this.updateProduct()
              this.colorPreselectedFromUrl = true
              this.forcePriceRecalculation()
            }
          }
        }
      },
      deep: true,
    },
  },
  created() {
    this.$nuxt.$emit('productConfigValidity', {
      isValid: true,
      pending: false,
      reason: null,
    })
    this.excludedSizes = get(this.product, 'content.product_sizes_excluded', [])
    this.sizeVariants = get(
      this.product,
      'content.product_configurations_priced[0].product_configuration_value_prices',
      [],
    ).map(item => ({ height: item.max_height, width: item.max_width }))
    this.sizeVariants.splice(1, 2)

    this.paramIndex = new Map()
    for (const p of this.parameters) {
      const item = p.items?.[0]
      const id = item?.attributes?.product_configuration_id
      if (id) this.paramIndex.set(id, item)
    }

    this.updateProductDebounced = debounce(() => this.updateProduct(), 150)

    this.initializeAirshaftValues()
  },
  methods: {
    initializeAirshaftValues() {
      const groups = this.groupedConfiguration?.filter(g => this.airShaft.ids.includes(g._id))
      groups?.forEach(group => {
        group?.configurations?.forEach(config => {
          if (!('airShaftValue' in config)) {
            this.$set(config, 'airShaftValue', null)
          }
        })
      })
    },
    addToCart() {
      this.$refs?.AddToCart?.$refs?.AddToCart?.click()
    },
    submitSize() {
      const configuration = this.configuration.map(c => ({ [c.code]: Number(c.selected) }))
      configuration.push({ weight: this.weight })

      this.$nuxt.$emit('updateProductByAttributes', {
        width: this.sizes.width,
        height: this.sizes.height,
        configuration: uniqBy(configuration, it => Object.keys(it)[0]),
        extras: { externalId: `${this.product.externalId}` },
      })
    },
    updateProduct() {
      const configuration = this.configuration.map(c => ({ [c.code]: Number(c.selected) }))
      configuration.push({ weight: this.weight })

      this.$nuxt.$emit('updateProductByAttributes', {
        width: this.productWidth,
        height: this.productHeight,
        configuration: uniqBy(configuration, it => Object.keys(it)[0]),
        extras: { externalId: `${this.product.externalId}` },
      })

      const selectedIds = new Set(this.configuration.filter(c => c.selected).map(c => c.product_configuration_id))
      Object.keys(this.airShaftValues).forEach(id => {
        if (!selectedIds.has(id)) this.$delete(this.airShaftValues, id)
      })
    },
    submitSizeDebounce: debounce(function() {
      this.submitSizeWithValidation()
    }, 400),
    async submitSizeWithValidation() {
      this.isValidatingSize = true
      this.sizeValidationError = null

      this.$nuxt.$emit('productConfigValidity', {
        isValid: false,
        pending: true,
        reason: null,
      })

      try {
        const configuration = this.configuration.map(c => ({ [c.code]: Number(c.selected) }))
        configuration.push({ weight: this.weight })

        const result = await this.$store.dispatch('product/UPDATE_PRODUCT_ATTRIBUTES', {
          id: this.product.id,
          config: {
            width: this.sizes.width,
            height: this.sizes.height,
            configuration: uniqBy(configuration, it => Object.keys(it)[0]),
            extras: { externalId: `${this.product.externalId}` },
          },
        })

        if (result.success && result.isValid) {
          this.sizeValidationError = null

          this.$nuxt.$emit('productConfigValidity', {
            isValid: true,
            pending: false,
            reason: null,
          })

          // Keep your existing emit that updates product (if you still want it)
          this.$nuxt.$emit('updateProductByAttributes', {
            width: this.sizes.width,
            height: this.sizes.height,
            configuration: uniqBy(configuration, it => Object.keys(it)[0]),
            extras: { externalId: `${this.product.externalId}` },
          })
        } else {
          const reason = result.error || 'Invalid size configuration'
          this.sizeValidationError = reason

          this.$nuxt.$emit('productConfigValidity', {
            isValid: false,
            pending: false,
            reason,
          })
        }
      } catch (error) {
        console.error('Size validation error:', error)
        this.sizeValidationError = 'Unable to validate size configuration'
        this.$nuxt.$emit('productConfigValidity', {
          isValid: false,
          pending: false,
          reason: 'Unable to validate size configuration',
        })
      } finally {
        this.isValidatingSize = false
      }
    },
    onSizeChange(size, key) {
      this.sizes[key] = size
      this.$emit('configurationChange', { configuration: this.configuration, sizes: this.sizes })
      this.submitSizeDebounce()
    },
    getClosestSize(combinations, inputWidth, inputHeight, isWidth) {
      let closestSizeWithExactWidth = null
      let closestSizeWithExactWidthDiff = Infinity
      let closestSizeWithExactHeight = null
      let closestSizeWithExactHeightDiff = Infinity
      let closestSize = null
      let closestSizeDiff = Infinity

      combinations.forEach(comb => {
        const widthDiff = Math.abs(inputWidth - comb.width)
        const heightDiff = Math.abs(inputHeight - comb.height)

        if (isWidth) {
          if (comb.width === inputWidth && heightDiff < closestSizeWithExactWidthDiff) {
            closestSizeWithExactWidth = comb
            closestSizeWithExactWidthDiff = heightDiff
          }
        } else {
          if (comb.height === inputHeight && widthDiff < closestSizeWithExactHeightDiff) {
            closestSizeWithExactHeight = comb
            closestSizeWithExactHeightDiff = widthDiff
          }
        }

        const diffScore = widthDiff + heightDiff
        if (diffScore < closestSizeDiff) {
          closestSize = comb
          closestSizeDiff = diffScore
        }
      })

      if (closestSizeWithExactHeight) return closestSizeWithExactHeight
      if (closestSizeWithExactWidth) return closestSizeWithExactWidth
      return closestSize
    },
    selectConfiguration(group, configuration) {
      const isGroupMultiselect = !group.required
      const selectedId = configuration.product_configuration_id

      for (const c of group.configurations) {
        const id = c.product_configuration_id
        const item = this.paramIndex.get(id)
        if (!item) continue

        if (isGroupMultiselect) {
          if (id === selectedId) item.attributes.selected = !item.attributes.selected
        } else {
          item.attributes.selected = id === selectedId && !item.attributes.selected
        }
      }

      this.updateProductDebounced()
      this.$emit('configurationChange', { configuration: this.configuration, sizes: this.sizes })
    },
    forcePriceRecalculation() {
      Object.keys(this.sizes).forEach(key => {
        this.onSizeChange(this.sizes[key], key)
      })
    },
    setAirshaftValue(configId, value) {
      if (value == null) {
        if (this.airShaftValues[configId] != null) this.$delete(this.airShaftValues, configId)
      } else {
        this.$set(this.airShaftValues, configId, value)
      }
    },
  },
}
